import { Request } from 'models/Search/Request';
import * as Constants from 'util/Constants';

/** Represents parts of the browser query string that are fixed and are always single strings. */
interface ParsedQueryStringFixed {
	keyword?: string;
	sort?: string;
	pg?: string;
	lp?: string;
	PageId?: string;
	lpurl?: string;
	mpp?: string;
	searchWithin?: string;
	is100Coverage?: string;
	indexName?: string;
}

/**
 * Represents the parts of the browser query string that are dynamic (the selected facets). Facets
 * can have multiple values, so the value of these is always an array of strings.
 */
interface ParsedQueryStringDynamic {
	[key: string]: string[];
}

type ParsedQueryString = ParsedQueryStringFixed & ParsedQueryStringDynamic;

/**
 * Parses the input query string and returns an object that can be used to build a search request.
 * The object returned will usually have the keys: `keyword`, `sort`, `pg`,`lp`,`lpurl`, `mpp`, and then more keys
 * for every selected facet.
 * @param search The input query string.
 */
function parseQueryStringToObject(search: string) {
	const params = new URLSearchParams(search);

	const parsed: ParsedQueryString = {};

	params.forEach((value, key) => {
		if (
			key === 'keyword' ||
			key === 'sort' ||
			key === 'pg' ||
			key === 'lp' ||
			key === 'PageId' ||
			key === 'lpurl' ||
			key === 'mpp' ||
			key === 'searchWithin' ||
			key === 'is100Coverage' ||
			key === 'indexName'
		) {
			// `keyword` is special and should never be turned into an array
			parsed[key] = value;
		} else {
			// everything else should be turned into an array

			if (!value) {
				// no useful value for this query param, so skip it
				return;
			}

			// multiple selections are split by commas, so split into an array
			const multipleValues = value.split(',');

			// and now handle any comma escaping - any single value that contained a comma is escaped to '::'
			for (let x = 0; x < multipleValues.length; ++x) {
				/* #region HCL */
				multipleValues[x] = multipleValues[x].replace(/%c%/g, ',');
				/* #endregion */
			}

			parsed[key] = multipleValues;
		}
	});

	return parsed;
}

/**
 * Parses the abosulte url into a `HawkClient` client search request object.
 * @param location The input location.
 * @param searchUrl The search page URL.
 */
export function parseLocation(
	location: Location,
	searchUrl: string = Constants.defaultSearchPageUrl,
): Partial<Request> {
	// TODO: Constants.defaultSearchPageUrl could be problematic if
	// someone defines a landing page with the custom url '/search'
	const searchRequest = parseSearchQueryString(location.search, searchUrl);

	return searchRequest;
}

/**
 * Parses the input query string into a `HawkClient` client search request object.
 * @param search The input query string.
 * @param searchUrl The search page URL.
 */
export function parseSearchQueryString(
	search: string,
	searchUrl: string = Constants.defaultSearchPageUrl,
): Partial<Request> {
	const queryObj = parseQueryStringToObject(search);

	// extract out components, including facet selections
	const {
		keyword,
		sort,
		pg,
		mpp,
		lp,
		PageId,
		lpurl,
		searchWithin,
		is100Coverage,
		indexName,
		...facetSelections
	} = queryObj;

	// ignore landing pages if keyword is passed
	const pageId = lp || PageId;
	const request = {
		Keyword: lpurl || pageId ? '' : keyword,

		SortBy: sort,
		PageNo: pg ? Number(pg) : undefined,
		MaxPerPage: mpp ? Number(mpp) : undefined,
		PageId: pageId ? Number(pageId) : undefined,
		CustomUrl: lpurl,
		SearchWithin: searchWithin,
		Is100CoverageTurnedOn: is100Coverage ? Boolean(is100Coverage) : undefined,
		FacetSelections: facetSelections,
		IndexName: indexName,
	};

	// customUrl have priority over keywords
	if (isPathLandingPage(location.pathname, searchUrl)) {
		// #region HCL
		request.Keyword = keyword;
		// #endregion
		request.CustomUrl = location.pathname.replace(searchUrl, '');
	}

	return request;
}

/**
 * Checks if the input path is a landing page and not the search page URL.
 * @param path The input path.
 * @param searchUrl The search page URL.
 */
export function isPathLandingPage(path: string, searchUrl: string): boolean {
	if (!path) {
		// if there's no path, this request can't be for a landing page
		return false;
	}

	if (!path.endsWith('/')) {
		path = path + '/';
	}

	if (!searchUrl.endsWith('/')) {
		searchUrl = searchUrl + '/';
	}

	return path !== searchUrl;
}

/**
 * Converts a search query object (such as one returned from `parseSearchQueryString`) and converts
 * it into a browser query string
 * @param queryObj The query object to convert to a query string.
 */
function convertObjectToQueryString(queryObj: ParsedQueryString) {
	const queryStringValues: string[] = [];

	for (const key in queryObj) {
		if (queryObj.hasOwnProperty(key)) {
			if (
				key === 'keyword' ||
				key === 'sort' ||
				key === 'pg' ||
				key === 'mpp' ||
				key === 'searchWithin' ||
				key === 'is100Coverage' ||
				key === 'indexName'
			) {
				const value = queryObj[key];

				if (value === undefined || value === null) {
					// if any of the special keys just aren't defined or are null, don't include them in
					// the query string
					continue;
				}

				if (typeof value !== 'string') {
					throw new Error(`${key} must be a string`);
				}

				// certain strings are special and are never arrays
				queryStringValues.push(key + '=' + encodeURIComponent(value));
			} else {
				const values = queryObj[key];

				// handle comma escaping - if any of the values contains a comma, they need to be escaped first
				const escapedValues: string[] = [];

				for (const unescapedValue of values) {
					/* #region HCL */
					escapedValues.push(unescapedValue.replace(/,/g, '%c%'));
					/* #endregion */
				}

				queryStringValues.push(key + '=' + escapedValues.join(','));
			}
		}
	}

	return '?' + queryStringValues.join('&');
}

/**
 * Converts a partial search request object into a browser query string.
 * @param searchRequest The search request object to convert.
 */
export function getSearchQueryString(searchRequest: Partial<Request>) {
	const searchQuery = {
		keyword: searchRequest.Keyword,
		sort: searchRequest.SortBy,
		pg: searchRequest.PageNo ? String(searchRequest.PageNo) : undefined,
		mpp: searchRequest.MaxPerPage ? String(searchRequest.MaxPerPage) : undefined,
		is100Coverage: searchRequest.Is100CoverageTurnedOn ? String(searchRequest.Is100CoverageTurnedOn) : undefined,
		searchWithin: searchRequest.SearchWithin,

		...searchRequest.FacetSelections,
	} as ParsedQueryString;

	return convertObjectToQueryString(searchQuery);
}
